import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { certificateActions, alertActions, regisActions } from '../../../store/actions'

/**
 * PCC 본인확인 V4 - 간편 회원가입용 (PC) 컴포넌트.
 *
 * PccSimple.jsx(V2)의 V4 전환 버전. PccV4.jsx와 동일한 V4 흐름이며,
 * 간편 회원가입에서 필요한 **휴대폰 번호 중복가입 체크(checkMobile)** 처리를 추가로 갖는다.
 *
 * V2(PccSimple.jsx)와의 차이점:
 *  - 파라미터 조회를 openPccRequestV4(memberId)로 호출 → reqNum이 member_id 기반 유니크 값
 *  - 조회 시점이 마운트가 아니라 본인인증 클릭(isPccOpen=true) 시점
 *  - 요청 팝업이 /pccCertPopupV4.htm, 표준창 action이 pcc_V3_j10_v4.jsp (응답의 standardWindowUrl 사용)
 *  - hidden form에 crypto_token_id 추가
 *  - 완료 시 retInfo 대신 crypto_token_id를 resultPccInfoV4에 전달
 *
 * props.bplcCd  : 값이 있으면 인증 성공 후 휴대폰 중복가입 체크(checkMobile) 수행
 * props.memberId: (옵션) 회원가입 흐름에서 입력한 아이디. 미전달 시 서버가 로그인 사용자/익명(ANON) 처리.
 */
const PccV4Simple = (props) => {
  // 한 번의 본인인증(open 사이클)당 팝업을 한 번만, 그리고 '새로 받은' reqInfo로만 열기 위한 가드.
  // (상세 주석은 PccV4.jsx 참고)
  const cycleRef = useRef({ armed: false, sawClear: false, opened: false })

  // 리스너 등록
  useEffect(() => {
    window.addEventListener('message', pccMessageFunc, false)
  }, [])

  // 리스너 해제 (미해제 시 윈도우에 계속 남아 과부하 발생)
  useEffect(
    () => () => {
      window.removeEventListener('message', pccMessageFunc, false)
    },
    [],
  )

  const pccMessageFunc = (e) => {
    if (e.data.message) {
      switch (e.data.message) {
        case 'pccWindowClose':
          props.onChange()
          break
        case 'pccComplete':
          // V4: 표준창이 회신한 crypto_token_id로 결과 조회
          props.resultPccInfoV4(document.getElementById('retCryptoTokenId').value)
          break
        default:
          break
      }
    }
  }

  // 본인인증 클릭(isPccOpen=true) 시점에: 이전 값 초기화 후, 그때의 memberId로 파라미터 요청.
  useEffect(() => {
    if (props.isPccOpen === true) {
      cycleRef.current = { armed: true, sawClear: false, opened: false }
      props.setInitialState() // reqInfo='' 로 초기화 → 이전(stale) 값으로 오픈되는 것 방지
      props.openPccRequestV4(props.memberId)
    } else {
      cycleRef.current = { armed: false, sawClear: false, opened: false }
    }
  }, [props.isPccOpen])

  // 새로 받은 reqInfo가 준비된 뒤에만 팝업 오픈.
  useEffect(() => {
    const c = cycleRef.current
    if (!c.armed || c.opened) return
    if (!props.pccInfo.reqInfo) {
      // 초기화(reqInfo='')가 반영된 것을 관측 → 이후 도착하는 값만 '새 값'으로 인정
      c.sawClear = true
      return
    }
    // 아직 초기화를 관측하지 못한 상태의 truthy 값은 이전 사이클의 잔존값이므로 무시
    if (!c.sawClear) return
    c.opened = true
    window.open(
      '/pccCertPopupV4.htm',
      'PCCV3Window',
      'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=no, copyhistory=no, width=415,height=600',
    )
  }, [props.isPccOpen, props.pccInfo.reqInfo])

  useEffect(() => {
    if (props.certResult === 'Y') {
      props.success({
        title: '확인',
        message: '본인인증이 정상 인증되었습니다.',
        params: {
          type: 'success',
          callback: () => {
            props.onChange()
            props.callBack && props.callBack(true)
            // 휴대폰 번호 중복 가입 체크
            if (props.bplcCd != null) {
              props.checkMobile('02', props.cellNo, props.certCi)
            }
          },
        },
      })
    } else if (props.certResult === 'N') {
      props.onChange()
      props.error({
        title: '확인',
        message: '본인인증을 실패했습니다.',
      })
    }
  }, [props.certResult])

  return (
    <React.Fragment>
      <form
        id="tranMgr"
        target="PCCV3Window"
        name="PCCV3WFRM"
        method="post"
        action={props.pccInfo.standardWindowUrl || 'https://pcc.siren24.com/pcc_V3/jsp/pcc_V3_j10_v4.jsp'}
      >
        <input type="hidden" id="verSion" name="verSion" value={props.pccInfo.verSion || '3'} readOnly />
        <input type="hidden" id="retUrl" name="retUrl" value={props.pccInfo.retUrl} readOnly />
        <input type="hidden" id="reqInfo" name="reqInfo" value={props.pccInfo.reqInfo} readOnly />
        <input type="hidden" id="crypto_token_id" name="crypto_token_id" value={props.pccInfo.cryptoTokenId} readOnly />
        {/* PccV4.jsx와 동일하게 integrity_value는 미전송 (pccCertPopupV4.htm에서도 주석 처리됨) */}
        {/*<input type="hidden" id="integrity_value" name="integrity_value" value={props.pccInfo.integrityValue} readOnly />*/}
        {/* 표준창이 회신하는 crypto_token_id 수신용 */}
        <input type="hidden" id="retCryptoTokenId" name="retCryptoTokenId" />
      </form>
    </React.Fragment>
  )
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    pccInfo: state.certificate,
    certResult: state.certificate?.resultInfo?.result,

    // 휴대폰번호 유무 체크를 위해 값 받음 (V4 결과도 ci1/cellNo 키로 정규화되어 내려옴)
    cellNo: state?.certificate?.resultInfo?.cellNo,
    certCi: state?.certificate?.resultInfo?.ci1,
  }
}

const actionCreators = {
  openPccRequestV4: certificateActions.openPccRequestV4,
  resultPccInfoV4: certificateActions.resultPccInfoV4,
  setInitialState: certificateActions.setInitialState,
  success: alertActions.success,
  error: alertActions.error,
  checkMobile: regisActions.checkMobile,
}

export default connect(mapStateToProps, actionCreators)(PccV4Simple)
